import { Button, Modal, Form, Input, Col, Table, Space, Select } from "antd";
import { useEffect, useState } from "react";
import { RootState } from "../../../app/store";
import {
  pendingCalculationUserRequestAsync,
  removeFromPendingListAsync,
  skipFromPendingListAsync,
} from "../../../features/userRequest/pendingCalculationUserRequestSlice";
import {
  skipAllPendingUserRequests,
  updateQRFeeUserRequest,
} from "../../../services/userRequestApi";
import UserRequestColumns from "./columns";

import "./styles.css";
import ContainerLayout from "../../common/containerLayout";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getCurrencies } from "../../../services/currencyApi";
import { InlineForm } from "../../../shared/components/InlineForm";
import { CurrencyDTO } from "../../../shared/dtos/currencyDTO";

const { Search } = Input;

export const UserRequestPendingTable = () => {
  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isMultiOmitModalOpen, setIsMultiOmitModalOpen] = useState(false);
  const [reload, setReload] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmLoading, setIsConfirmLoading] = useState(false);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [currencies, setCurrencies] = useState<CurrencyDTO[]>([]);
  const dispatch = useAppDispatch();
  const {
    value: { userRequestByDateDto: userRequestByDateValue },
    status,
  } = useAppSelector((state: RootState) => state.pendingCalculationUserRequest);
  const userRequestByDateTotal: number = useAppSelector(
    (state: RootState) => state.pendingCalculationUserRequest.value.total,
  );

  const [form] = Form.useForm();

  const fetchData = async () => {
    const data = await getCurrencies();
    setCurrencies(data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    dispatch(
      pendingCalculationUserRequestAsync({
        page: activePage,
        take: pageSize,
        searchText,
      }),
    );
  }, [dispatch, activePage, pageSize, reload, searchText]);

  const onPageChange = async (page: number) => {
    setActivePage(page);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    form.resetFields();
  };

  const calculateRequest = async () => {
    const { total, currencyId } = form.getFieldsValue();

    if (!selectedId || isNaN(total)) {
      alert("Dolares y Pesos deben ser numeros");
      return;
    }

    if (!currencyId) {
      alert("Debe seleccionar una denominacion");
      return;
    }

    const currencyCode = currencies.find(
      (currency) => currency.id === currencyId,
    )?.code;

    try {
      await dispatch(
        removeFromPendingListAsync({
          userRequestId: selectedId,
          total,
          currencyCode,
        }),
      );
    } catch (e) {
    } finally {
      setReload(!reload);
      setOpenModal(false);
      setSelectedId(null);
    }
  };

  const skipRequest = (id?: number | null) => {
    if (id) {
      dispatch(skipFromPendingListAsync(id)).then(() => {
        setReload(!reload);
      });
    }
  };

  const items = (id: number, qrFee: number) => {
    return [
      {
        key: "1",
        label: "Cobrar",
        onClick: () => {
          setSelectedId(id);
          setOpenModal(true);
        },
      },
      {
        key: "2",
        label: "Editar Comision",
        onClick: () => {
          setSelectedId(id);
          setIsQRModalOpen(true);
          form.setFieldValue("qrFee", qrFee);
        },
      },
    ];
  };

  const handleOk = async () => {
    setIsConfirmLoading(true);
    const userRequestIds = new Array<number>();

    for (const userRequest of userRequestByDateValue) {
      userRequestIds.push(userRequest.id ?? 0);
    }

    await skipAllPendingUserRequests(userRequestIds).then(() => {
      setReload(!reload);
    });

    setIsModalOpen(false);
    setIsConfirmLoading(false);
  };

  const handleMultiOmitOk = async () => {
    setIsConfirmLoading(true);
    const userRequestIds = selectedRowKeys.map((key) => Number(key));

    await skipAllPendingUserRequests(userRequestIds).then(() => {
      setSelectedRowKeys([]);
      setReload(!reload);
    });

    setIsMultiOmitModalOpen(false);
    setIsConfirmLoading(false);
  };

  const handleQRFeeOk = async () => {
    const { qrFee } = form.getFieldsValue();

    setIsConfirmLoading(true);
    await updateQRFeeUserRequest({
      userRequestId: selectedId ?? 0,
      qrFee: +qrFee,
    }).then(() => {
      setReload(!reload);
      setIsQRModalOpen(false);
      setIsConfirmLoading(false);
    });
  };

  const handleClick = (search: string) => {
    setSearchText(search);
    setActivePage(1);
  };

  return (
    <ContainerLayout title="Cierres" showNotification={false}>
      <Col span={24}>
        <Space>
          <Search
            type="text"
            placeholder="Buscar por..."
            onSearch={handleClick}
            loading={status === "loading"}
            disabled={status === "loading"}
            style={{ width: "300px" }}
          />
          <Button
            onClick={() => setIsModalOpen(!isModalOpen)}
            disabled={status === "loading"}
          >
            Omitir todos
          </Button>
          <Button
            onClick={() => setIsMultiOmitModalOpen(true)}
            disabled={status === "loading" || selectedRowKeys.length === 0}
            type="primary"
            danger
          >
            Omitir seleccionados ({selectedRowKeys.length})
          </Button>
        </Space>
      </Col>
      <Col span={24} style={{ width: "100px", height: "83vh" }}>
        <Table
          loading={status === "loading"}
          rowKey="id"
          locale={{ emptyText: "No hay datos" }}
          dataSource={userRequestByDateValue}
          columns={UserRequestColumns({ items, skipRequest })}
          rowSelection={{
            selectedRowKeys,
            onChange: (newSelectedRowKeys: React.Key[]) => {
              setSelectedRowKeys(newSelectedRowKeys);
            },
          }}
          pagination={{
            total: userRequestByDateTotal,
            showSizeChanger: true,
            pageSize: pageSize,
            onShowSizeChange: (current, size) => {
              setPageSize(size);
              setActivePage(1);
            },
            onChange: onPageChange,
            showTotal: (total) => `Total ${total} viajes`,
            size: "default",
          }}
          scroll={{ x: 600, y: "calc(-250px + 96vh)" }}
        />
      </Col>
      <Modal
        open={isModalOpen}
        onOk={handleOk}
        onCancel={() => setIsModalOpen(false)}
        confirmLoading={confirmLoading}
        okText={confirmLoading ? "Omitiendo" : "Ok"}
      >
        <b>Desea omitir los viajes listados?</b>
      </Modal>
      <Modal
        open={isMultiOmitModalOpen}
        onOk={handleMultiOmitOk}
        onCancel={() => setIsMultiOmitModalOpen(false)}
        confirmLoading={confirmLoading}
        okText={confirmLoading ? "Omitiendo" : "Ok"}
      >
        <b>Desea omitir los {selectedRowKeys.length} viajes seleccionados?</b>
      </Modal>
      <Modal
        title="Comision en porcentaje"
        open={isQRModalOpen}
        onOk={form.submit}
        onCancel={() => setIsQRModalOpen(false)}
        confirmLoading={confirmLoading}
        okText={confirmLoading ? "Editando" : "Editar Comision"}
      >
        <Form
          onFinish={handleQRFeeOk}
          layout="vertical"
          name="trip"
          scrollToFirstError
          form={form}
        >
          <Form.Item
            name="qrFee"
            label="Comision en porcentaje"
            tooltip="Comision en porcentaje"
            rules={[
              {
                required: true,
                message: "Ingresar comision en porcentaje",
              },
            ]}
          >
            <Input type="number" placeholder="100" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Cierre de viaje"
        centered
        open={openModal}
        onOk={form.submit}
        onCancel={handleCloseModal}
      >
        <Form
          layout="vertical"
          name="trip"
          scrollToFirstError
          onFinish={calculateRequest}
          form={form}
          initialValues={{
            total: 0,
            currencyId: null,
          }}
        >
          <InlineForm>
            <Form.Item
              name="total"
              label="Total del viaje"
              tooltip="Total del viaje"
              rules={[{ required: true, message: "Ingrese una cantidad" }]}
            >
              <Input type="number" />
            </Form.Item>
            {currencies && (
              <Form.Item
                name="currencyId"
                label="Denominación"
                tooltip="Enviar cierre denominacion seleccionada"
                rules={[
                  {
                    required: true,
                    message: "Por favor seleccione un servicio!",
                  },
                ]}
              >
                <Select placeholder="Selecione un tipo">
                  {currencies.map((currency: CurrencyDTO) => (
                    <Select.Option key={currency?.id} value={currency?.id}>
                      {currency?.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </InlineForm>
        </Form>
      </Modal>
    </ContainerLayout>
  );
};

export default UserRequestPendingTable;
